/**
 * CheckInCampaignCard — admin trigger for the 3-email marketing sequence
 * that pushes existing practices to turn on the 24-hour Post-Treatment
 * Check-In feature.
 *
 * Behavior:
 *   • Stats button — refreshes the counts (always safe to click)
 *   • Start Campaign button — fires Email 1 to every eligible practice
 *     RIGHT NOW. Emails 2 and 3 are scheduled to fire 4 and 7 days later
 *     by the existing 6-hourly drip scheduler.
 *   • Practices that already turned on check-ins are auto-skipped from
 *     emails 2 and 3.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Mail, BarChart3, Send, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckInCampaignCard({ adminToken }) {
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  const loadStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/checkin-campaign/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`Stats failed: ${res.status}`);
      setStats(await res.json());
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => { loadStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const start = async () => {
    const ok = window.confirm(
      `This will immediately send EMAIL 1 of the Check-In marketing campaign to EVERY practice in the database that has an email address.\n\n` +
      `Emails 2 and 3 will follow automatically 4 days and 7 days later — but only to practices that haven't activated check-ins by then.\n\n` +
      `Each practice can only receive each email ONCE — re-running this button is safe and will only send to new practices.\n\nProceed?`
    );
    if (!ok) return;
    setBusy(true);
    setErr(null);
    setReport(null);
    try {
      const res = await fetch(`${API}/api/admin/checkin-campaign/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
      });
      if (!res.ok) throw new Error(`Start failed: ${res.status} ${res.statusText}`);
      const data = await res.json();
      setReport(data);
      await loadStats();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const StatBox = ({ label, value, color = 'blue' }) => {
    const tones = {
      blue:    'bg-blue-50 border-blue-200 text-blue-900',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
      amber:   'bg-amber-50 border-amber-200 text-amber-900',
      slate:   'bg-slate-50 border-slate-200 text-slate-900',
    };
    return (
      <div className={`${tones[color]} border-2 rounded-lg p-3 text-center`}>
        <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
        <p className="text-2xl font-extrabold mt-1">{value ?? '—'}</p>
      </div>
    );
  };

  return (
    <div
      data-testid="admin-checkin-campaign-card"
      className="bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-200 rounded-xl p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="bg-sky-600 text-white rounded-lg p-2.5">
          <Mail className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-sky-900">Check-In Marketing Campaign</h3>
          <p className="text-xs text-sky-800 mt-0.5 max-w-2xl leading-snug">
            3-email sequence sent to every practice with an email address.
            Drives practices to <b>activate the 24-hour Post-Treatment Check-In</b>.
            Personalized with first name + practice name. Day 0, Day 4, Day 7.
            Practices that activate are auto-skipped from emails 2 &amp; 3.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatBox label="Practices w/ email" value={stats?.withEmail}      color="slate" />
        <StatBox label="Campaign started"   value={stats?.campaignStarted} color="blue" />
        <StatBox label="Email 1 sent"       value={stats?.email1Sent}      color="blue" />
        <StatBox label="Email 2 sent"       value={stats?.email2Sent}      color="blue" />
        <StatBox label="Email 3 sent"       value={stats?.email3Sent}      color="blue" />
        <StatBox label="Check-ins activated" value={stats?.checkInsActivated} color="emerald" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={loadStats}
          disabled={busy}
          className="bg-white hover:bg-sky-50 border-2 border-sky-300 text-sky-800 px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-bold disabled:opacity-50"
          data-testid="checkin-refresh-stats"
        >
          <RefreshCw className="w-4 h-4" /> Refresh stats
        </button>
        <button
          onClick={start}
          disabled={busy}
          className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold disabled:opacity-50"
          data-testid="checkin-start-campaign"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {stats?.email1Sent ? 'Send Email 1 to new practices' : 'Start Campaign (send Email 1 now)'}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Email 2 fires <b>~4 days</b> after the campaign starts; Email 3 fires <b>~7 days</b> after.
          They are sent automatically by the background scheduler that runs every 6 hours.
          You don't need to come back and click anything.
        </div>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3" data-testid="checkin-error">
          {err}
        </div>
      )}

      {report && (
        <div className="bg-white border border-sky-200 rounded-lg p-3 text-sm" data-testid="checkin-report">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <p className="font-extrabold text-slate-900">Campaign started</p>
          </div>
          <p className="text-xs text-slate-700 mb-2">
            <b>{report.started}</b> practices entered the campaign · {report.email1?.sent ?? 0} Email 1 sent ·{' '}
            {report.email1?.alreadySent ?? 0} already had it ·{' '}
            {report.email1?.alreadyActivated ?? 0} already activated ·{' '}
            {report.email1?.failed ?? 0} failed
          </p>
          {report.skipped?.noEmail > 0 && (
            <p className="text-xs text-slate-500">Skipped {report.skipped.noEmail} practice(s) with no email on file.</p>
          )}
          {!!(report.failures || []).length && (
            <details className="mt-2">
              <summary className="text-xs font-bold text-rose-700 cursor-pointer">Show first 10 failures</summary>
              <ul className="text-xs text-slate-700 mt-1 space-y-0.5">
                {report.failures.map((f, i) => (
                  <li key={i}><b>{f.email || '(no email)'}</b>: {f.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
